import { getReminderPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useReminderSend } from "@/queries/reminders.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderEntityType } from "@/types/reminder.types.ts";
import {
  Button,
  Checkbox,
  Modal,
  ModalProps,
  Stack,
  Switch,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface ReminderSendModalProps extends ModalProps {
  recipients: ApiEmployee[];
  entityType: ReminderEntityType;
  entityIds: number[];
  onSuccess?: () => void;
}

export function ReminderSendModal({
  recipients,
  entityType,
  entityIds,
  onSuccess,
  ...props
}: ReminderSendModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();
  const { renderEmployeeName } = useFormatter();

  const sendReminder = useReminderSend();
  const [customizeMessage, setCustomizeMessage] = useState(false);
  const reminderPlaceholders = getReminderPlaceholderOptions();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    channels: ApiNotificationChannelType[];
    custom_subject: string;
    custom_body: string;
  }>({
    defaultValues: {
      channels: [
        ApiNotificationChannelType.in_app,
        ApiNotificationChannelType.email,
        ApiNotificationChannelType.slack,
      ],
      custom_subject: "",
      custom_body: "",
    },
  });

  const caption = useMemo(() => {
    if (recipients.length === 0) return t("core:to_everyone");
    const names = recipients.map((r) => renderEmployeeName(r)).join(", ");
    return names.length > 100 ? names.slice(0, 97) + "..." : names;
  }, [recipients, renderEmployeeName, t]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await sendReminder.mutateAsync({
        entity_type: entityType,
        entity_ids: entityIds,
        recipient_employee_ids: recipients.map((r) => r.id),
        channels: data.channels,
        custom_subject: customizeMessage && data.custom_subject ? data.custom_subject : null,
        custom_body: customizeMessage && data.custom_body ? data.custom_body : null,
      });
      analyticsService.trackEvent(analyticEvents.reminders.sent, {
        [analyticProperties.source]: entityType,
      });
      onSuccess?.();
      close(id);
    } catch {
      return;
    }
  });

  const toggleChannel = (
    channel: ApiNotificationChannelType,
    current: ApiNotificationChannelType[],
    onChange: (value: ApiNotificationChannelType[]) => void
  ) => {
    if (channel === ApiNotificationChannelType.in_app) return;
    onChange(
      current.includes(channel) ? current.filter((c) => c !== channel) : [...current, channel]
    );
  };

  return (
    <Modal
      {...props}
      title={t("core:reminder")}
      caption={caption}
      withCloser
      actions={
        <Stack gap={"smd"} direction={"horizontal"} justify={"between"}>
          <ServerErrorField errors={errors} />
          <Button
            className={"w-full"}
            type={"submit"}
            size={"lg"}
            onClick={onSubmit}
            isLoading={sendReminder.isPending}
          >
            {t("core:send_reminders")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"md"}>
        <Stack gap={"sm"}>
          <Title size={"xs"} header={t("Channels")} />
          <Controller
            control={control}
            name="channels"
            render={({ field: { value, onChange } }) => (
              <Stack gap={"sm"}>
                <Checkbox
                  title={t("core:in_app")}
                  variant={"card"}
                  checked={value.includes(ApiNotificationChannelType.in_app)}
                  disabled
                />
                <Checkbox
                  title={"Email"}
                  variant={"card"}
                  checked={value.includes(ApiNotificationChannelType.email)}
                  onChange={() => toggleChannel(ApiNotificationChannelType.email, value, onChange)}
                />
                <Checkbox
                  title={"Slack"}
                  variant={"card"}
                  checked={value.includes(ApiNotificationChannelType.slack)}
                  onChange={() => toggleChannel(ApiNotificationChannelType.slack, value, onChange)}
                />
                <Checkbox
                  title={t("core:messenger")}
                  variant={"card"}
                  checked={value.includes(ApiNotificationChannelType.messenger)}
                  onChange={() =>
                    toggleChannel(ApiNotificationChannelType.messenger, value, onChange)
                  }
                />
              </Stack>
            )}
          />
        </Stack>
        <Stack gap={"sm"}>
          <Title size={"xs"} header={t("core:reminder_message")} />
          <Switch
            checked={customizeMessage}
            onChange={setCustomizeMessage}
            title={t("core:customize_reminder_message")}
            size={"sm"}
          />
          {customizeMessage && (
            <Stack gap={"sm"}>
              <Controller
                control={control}
                name="custom_subject"
                render={({ field: { value, onChange } }) => (
                  <InputWithVariableInsert
                    label={t("core:email_subject")}
                    value={value}
                    onInput={(e) => onChange(e.currentTarget.value)}
                    placeholder={t("core:enter_custom_subject")}
                    variableOptions={reminderPlaceholders}
                  />
                )}
              />
              <Controller
                control={control}
                name="custom_body"
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value}
                    onChange={onChange}
                    placeholder={t("core:enter_custom_body")}
                    placeholderOptions={reminderPlaceholders}
                    minHeight={120}
                    hideHeadings
                  />
                )}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
